import type { ISO8601DateOnly } from './common.ts';
import type { AppID, FacilityID } from './db.ts';

// Orange = needs you, yellow = coming up, green = all clear. All clear means the tool has nothing pending; we have no
// way to tell whether work is ahead or behind, so it makes no claim about progress. Its items are facts worth knowing on
// a quiet day, like a filing that's done for the year, and aren't counted.
export type PlatformStatus = 'needs' | 'soon' | 'clear';

// The pages a status can send the user to, in each app, besides those that need more than a facility (see PlatformLink).
export type PlatformPages = {
  tri: 'status' | 'products' | 'usage' | 'thresholds' | 'releases' | 'formR' | 'formRSubmit';
  emissions: 'overview' | 'units';
  tierii: 'overview' | 'products' | 'inventoryQuestionnaire' | 'inventoryThreshold' | 'reportForm' | 'export';
  hazwaste: 'home';
  compliance: 'facilityTasks';
  parsing: 'permitIndexing';
  determinations: 'home' | 'portal' | 'complianceIssues';
};

// Where to go to deal with a status. The page opens at `facilityID`, or, when null, at whatever Mission Control is
// showing: the facility, or for a tenant, all of its facilities in apps that can show several at once.
export type PlatformLink =
  | { [A in AppID]: { app: A; page: PlatformPages[A]; facilityID: FacilityID | null } }[AppID]
  // A unit's questionnaire, or with no unit, the overview of every unit's. The unit's ID is an EmissionsUnitID, which
  // isn't among the cross-app types.
  | {
      app: 'emissions';
      page: 'questionnaire';
      facilityID: FacilityID | null;
      emissionsUnit: { emissionsUnitID: number; name: string } | null;
    };

// `due` is shown after the text, e.g. "Q3 emissions report, due Oct 30", and feeds "Next up". `link` is null when
// there's nothing more specific to do about it than open the tool.
export type PlatformToolStatusItem =
  | { status: 'needs'; text: string; count: number; due: ISO8601DateOnly | null; link: PlatformLink | null }
  | { status: 'soon'; text: string; due: ISO8601DateOnly | null; link: PlatformLink | null }
  | { status: 'clear'; text: string; link: PlatformLink | null };

// The year TRI and Tier II are reporting on: the previous calendar year.
export function platformReportingYear(now: Date): number {
  return now.getUTCFullYear() - 1;
}

// Not every tenant has access to every app. Without access, a tool is shown greyed out and has no statuses.
export type PlatformToolAccess = { granted: true; items: PlatformToolStatusItem[] } | { granted: false };

export type PlatformToolStatusAggregated = Record<AppID, PlatformToolAccess>;

export function platformToolStatusAggregatedDefault(): PlatformToolStatusAggregated {
  return {
    tri: { granted: false },
    emissions: { granted: false },
    tierii: { granted: false },
    hazwaste: { granted: false },
    compliance: { granted: false },
    parsing: { granted: false },
    determinations: { granted: false },
  };
}
